import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import dayjs from 'dayjs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Form, message, Modal } from 'antd';
import { useCanCheck } from '@/ability/helpers';
import {
    PROJECT_MILESTONES_CREATE,
    PROJECT_MILESTONES_UPDATE,
    PROJECT_MILESTONES_DELETE,
    PROJECT_PHASES_CREATE,
    PROJECT_PHASES_UPDATE,
    PROJECT_PHASES_DELETE,
    PROJECT_PAYMENTS_VIEW,
} from '@/ability';
import {
    getProjectMilestones,
    getProjectMilestonesForPhase,
    getProjectPhases,
    createProjectMilestone,
    updateProjectMilestone,
    deleteProjectMilestone,
    createMilestoneDeliverable,
    updateMilestoneDeliverable,
    deleteMilestoneDeliverable,
    createProjectPhase,
    updateProjectPhase,
    deleteProjectPhase,
} from '@services/projects.service';
import { invalidateProjectQueries, removeProjectQueriesExceptMilestones } from './projectQueryInvalidation';
import { PROJECT_DETAIL_QUERY_KEY, useProjectDetail } from './detail/useProjectDetail';
import { contentToApiMentions, resolveDescriptionMentionsForDisplay } from '@utils/richTextMentions';
import {
    getLastMilestoneDueDate,
    getMilestoneDueDateEditBounds,
    createMilestoneDueDateValidator,
    createMilestoneDisabledDate,
    createMilestoneEditDisabledDate,
    createMilestoneEditDueDateValidator,
    buildMilestoneDescription,
    buildRemoveMilestoneAttachmentPatchPayload,
    buildPhaseDescription,
    collectSprintsFromProjectMilestones,
    getPhaseDescriptionText,
    getPhaseAttachmentList,
    getMilestoneDescriptionText,
    MILESTONE_UNASSIGNED_PHASE_KEY,
    getMilestoneAttachmentList,
    resolveAttachmentUrl,
    isLikelyFileAttachmentUrl,
    isLikelyImageAttachmentUrl,
    getAttachmentFileExtension,
} from '@hooks/projects/projectHelpers';

export const PROJECT_MILESTONES_QUERY_KEY = (projectId) => ['projects', projectId, 'milestones'];

/** PATCH `status` values accepted by the API (see validation message on milestone update). */
export const MILESTONE_STATUS_API_VALUES = ['pending', 'in_progress', 'completed', 'delayed'];

/** Matches API validation message for PATCH milestone `status`. */
export const MILESTONE_STATUS_RULE_MESSAGE =
    'status must be one of the following values: pending, in_progress, completed, delayed';

export const MILESTONE_STATUS_OPTIONS = [
    { value: 'pending', label: 'Pending' },
    { value: 'in_progress', label: 'In progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'delayed', label: 'Delayed' },
];

function normalizeMilestoneStatusForForm(raw) {
    const s = String(raw ?? '')
        .trim()
        .toLowerCase();
    if (MILESTONE_STATUS_API_VALUES.includes(s)) return s;
    if (s === 'active' || s === '') return 'in_progress';
    if (s === 'pending_completion') return 'pending';
    return 'pending';
}
const MENTION_USERS_LIMIT = 100;

/**
 * Milestone API + React Query: list (optional), create, update.
 * @param {string} projectId
 * @param {{ enabled?: boolean }} [options] — `enabled: false` when milestones come from GET /projects/:id only (milestones tab).
 */
export function useProjectMilestones(projectId, options = {}) {
    const { enabled = true, project: projectProp } = options;
    const queryClient = useQueryClient();

    const { data: projectFromQuery } = useProjectDetail(projectId, { enabled: !projectProp && !!projectId });
    const project = projectProp || projectFromQuery;
    const useMilestonesEnabled = project ? project.capabilities?.useMilestones !== false : false;

    const {
        data: milestones = [],
        isLoading,
        isError,
        refetch,
    } = useQuery({
        queryKey: PROJECT_MILESTONES_QUERY_KEY(projectId),
        queryFn: () => getProjectMilestones(projectId),
        enabled: !!projectId && enabled && useMilestonesEnabled,
    });

    const createMutation = useMutation({
        mutationFn: (body) => {
            const files = body.files;
            const linkAttachments = Array.isArray(body.attachments) ? body.attachments : [];
            if (files?.length) {
                const formData = new FormData();
                formData.append('name', typeof body.name === 'string' ? body.name : '');
                if (body.dueDate) formData.append('dueDate', body.dueDate);
                if (body.phaseId !== undefined) {
                    formData.append('phaseId', body.phaseId == null || body.phaseId === '' ? '' : String(body.phaseId));
                }
                if (body.description) formData.append('description', JSON.stringify(body.description));
                if (linkAttachments.length > 0) formData.append('attachmentUrls', JSON.stringify(linkAttachments));
                files.forEach((file) => formData.append('files', file));
                return createProjectMilestone(projectId, formData);
            }
            const { files: _f, attachments: _a, ...rest } = body;
            return createProjectMilestone(projectId, { ...rest, ...(linkAttachments.length > 0 && { attachments: linkAttachments }) });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'phases'] });
            queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'milestones'] });
        },
    });

    const createMilestone = (body, callbacks = {}) => {
        createMutation.mutate(body, {
            onSuccess: callbacks.onSuccess,
            onError: callbacks.onError,
        });
    };

    const updateMutation = useMutation({
        mutationFn: ({ milestoneId, body }) => {
            const files = body.files;
            const linkAttachments = Array.isArray(body.attachments) ? body.attachments : [];
            const useMultipart = (files && files.length > 0) || linkAttachments.length > 0 || body.useMultipart;
            if (useMultipart) {
                const formData = new FormData();
                formData.append('name', typeof body.name === 'string' ? body.name : '');
                if (body.dueDate) formData.append('dueDate', body.dueDate);
                if (body.isActive !== undefined) formData.append('isActive', String(body.isActive));
                if (body.status !== undefined && body.status !== null) formData.append('status', String(body.status));
                if (body.phaseId !== undefined) {
                    formData.append('phaseId', body.phaseId == null || body.phaseId === '' ? '' : String(body.phaseId));
                }
                if (body.description) formData.append('description', JSON.stringify(body.description));
                if (linkAttachments.length > 0) formData.append('attachmentUrls', JSON.stringify(linkAttachments));
                if (files && files.length > 0) {
                    files.forEach((file) => formData.append('files', file));
                }
                return updateProjectMilestone(projectId, milestoneId, formData);
            }
            const { files: _f, attachments: _a, useMultipart: _u, ...rest } = body;
            return updateProjectMilestone(projectId, milestoneId, rest);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'phases'] });
            queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'milestones'] });
        },
    });

    const updateMilestone = (milestoneId, body, callbacks = {}) => {
        updateMutation.mutate(
            { milestoneId, body },
            { onSuccess: callbacks.onSuccess, onError: callbacks.onError },
        );
    };

    const deleteMutation = useMutation({
        mutationFn: (milestoneId) => deleteProjectMilestone(projectId, milestoneId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'phases'] });
            queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'milestones'] });
        },
    });

    const deleteMilestone = (milestoneId, callbacks = {}) => {
        deleteMutation.mutate(milestoneId, {
            onSuccess: callbacks.onSuccess,
            onError: callbacks.onError,
        });
    };

    const createPhaseMutation = useMutation({
        mutationFn: (body) => {
            const files = body.files;
            const linkAttachments = Array.isArray(body.attachments) ? body.attachments : [];
            const title = typeof body.title === 'string' ? body.title : '';
            const description = typeof body.description === 'string' ? body.description : '';
            const useMultipart =
                (Array.isArray(files) && files.length > 0) || linkAttachments.length > 0;
            if (useMultipart) {
                const formData = new FormData();
                formData.append('title', title);
                if (description) formData.append('description', description);
                if (linkAttachments.length > 0) formData.append('attachmentUrls', JSON.stringify(linkAttachments));
                (files ?? []).forEach((file) => formData.append('files', file));
                return createProjectPhase(projectId, formData);
            }
            return createProjectPhase(projectId, {
                title,
                ...(description ? { description } : {}),
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'phases'] });
        },
    });

    const updatePhaseMutation = useMutation({
        mutationFn: ({ phaseId, body }) => {
            const files = body.files;
            const linkAttachments = Array.isArray(body.attachments) ? body.attachments : [];
            const useMultipart =
                (Array.isArray(files) && files.length > 0) || linkAttachments.length > 0;
            if (useMultipart) {
                const formData = new FormData();
                if (body.title != null) formData.append('title', String(body.title));
                if (body.description != null) formData.append('description', String(body.description));
                if (linkAttachments.length > 0) formData.append('attachmentUrls', JSON.stringify(linkAttachments));
                (files ?? []).forEach((file) => formData.append('files', file));
                return updateProjectPhase(projectId, phaseId, formData);
            }
            const { files: _f, attachments: _a, useMultipart: _u, ...rest } = body;
            return updateProjectPhase(projectId, phaseId, {
                ...rest,
                ...(linkAttachments.length > 0 && { attachments: linkAttachments }),
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'phases'] });
        },
    });

    const createPhase = (body, callbacks = {}) => {
        createPhaseMutation.mutate(body, {
            onSuccess: callbacks.onSuccess,
            onError: callbacks.onError,
        });
    };

    const updatePhase = (phaseId, body, callbacks = {}) => {
        updatePhaseMutation.mutate(
            { phaseId, body },
            { onSuccess: callbacks.onSuccess, onError: callbacks.onError },
        );
    };

    const createDeliverableMutation = useMutation({
        mutationFn: ({ milestoneId, value }) =>
            createMilestoneDeliverable(projectId, milestoneId, { value }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'milestones'] }),
    });

    const updateDeliverableMutation = useMutation({
        mutationFn: ({ milestoneId, deliverableId, isMarked }) =>
            updateMilestoneDeliverable(projectId, milestoneId, deliverableId, { isMarked }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'milestones'] }),
    });

    const editDeliverableMutation = useMutation({
        mutationFn: ({ milestoneId, deliverableId, value }) =>
            updateMilestoneDeliverable(projectId, milestoneId, deliverableId, { value }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'milestones'] }),
    });

    const deleteDeliverableMutation = useMutation({
        mutationFn: ({ milestoneId, deliverableId }) =>
            deleteMilestoneDeliverable(projectId, milestoneId, deliverableId),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'milestones'] }),
    });

    const createMilestoneDeliverableFn = useCallback(
        (milestoneId, value, callbacks = {}) => {
            createDeliverableMutation.mutate(
                { milestoneId, value: String(value ?? '').trim() },
                { onSuccess: callbacks.onSuccess, onError: callbacks.onError },
            );
        },
        [createDeliverableMutation],
    );

    const updateMilestoneDeliverableMarkedFn = useCallback(
        (milestoneId, deliverableId, isMarked, callbacks = {}) => {
            updateDeliverableMutation.mutate(
                { milestoneId, deliverableId, isMarked },
                { onSuccess: callbacks.onSuccess, onError: callbacks.onError },
            );
        },
        [updateDeliverableMutation],
    );

    const editMilestoneDeliverableFn = useCallback(
        (milestoneId, deliverableId, value, callbacks = {}) => {
            editDeliverableMutation.mutate(
                { milestoneId, deliverableId, value: String(value ?? '').trim() },
                { onSuccess: callbacks.onSuccess, onError: callbacks.onError },
            );
        },
        [editDeliverableMutation],
    );

    const deleteMilestoneDeliverableFn = useCallback(
        (milestoneId, deliverableId, callbacks = {}) => {
            deleteDeliverableMutation.mutate(
                { milestoneId, deliverableId },
                { onSuccess: callbacks.onSuccess, onError: callbacks.onError },
            );
        },
        [deleteDeliverableMutation],
    );

    return {
        milestones: Array.isArray(milestones) ? milestones : [],
        isLoading,
        isError,
        refetch,
        createMilestone,
        createPending: createMutation.isPending,
        updateMilestone,
        updateMilestonePending: updateMutation.isPending,
        deleteMilestone,
        deleteMilestonePending: deleteMutation.isPending,
        deleteMilestone,
        deleteMilestonePending: deleteMutation.isPending,
        createPhase,
        createPhasePending: createPhaseMutation.isPending,
        updatePhase,
        updatePhasePending: updatePhaseMutation.isPending,
        createMilestoneDeliverable: createMilestoneDeliverableFn,
        createDeliverablePending: createDeliverableMutation.isPending,
        updateMilestoneDeliverableMarked: updateMilestoneDeliverableMarkedFn,
        updateDeliverablePending: updateDeliverableMutation.isPending,
        editMilestoneDeliverable: editMilestoneDeliverableFn,
        editDeliverablePending: editDeliverableMutation.isPending,
        deleteMilestoneDeliverable: deleteMilestoneDeliverableFn,
        deleteDeliverablePending: deleteDeliverableMutation.isPending,
    };
}

/**
 * Project → Milestones tab UI state and create flow. Uses `useProjectMilestones(..., { enabled: false })`
 * so the tab reads milestones from GET /projects/:id; mutations refresh via project invalidation.
 */
export function useProjectMilestonesTab(projectId, project) {
    const isRelaxing = project?.sprintDateRule !== 'strict' && project?.settings?.sprintDateRule !== 'strict';

    const can = useCanCheck();
    const queryClient = useQueryClient();
    const canCreateMilestone = can(PROJECT_MILESTONES_CREATE);
    const canEditMilestone = can(PROJECT_MILESTONES_UPDATE);
    const canDeleteMilestone = can(PROJECT_MILESTONES_DELETE);
    const canCreatePhase = can(PROJECT_PHASES_CREATE);
    const canEditPhase = can(PROJECT_PHASES_UPDATE);
    const canDeletePhase = can(PROJECT_PHASES_DELETE);
    const canViewPayments = can(PROJECT_PAYMENTS_VIEW);

    const {
        createMilestone,
        createPending,
        updateMilestone,
        updateMilestonePending,
        deleteMilestone,
        deleteMilestonePending,
        createPhase,
        createPhasePending,
        updatePhase,
        updatePhasePending,
        createMilestoneDeliverable,
        createDeliverablePending,
        updateMilestoneDeliverableMarked,
        updateDeliverablePending,
        editMilestoneDeliverable,
        editDeliverablePending,
        deleteMilestoneDeliverable,
        deleteDeliverablePending,
    } = useProjectMilestones(projectId, { enabled: false });

    const [selectedPhaseId, setSelectedPhaseId] = useState(null);
    const [selectedMilestoneId, setSelectedMilestoneId] = useState(null);

    const usePhasesEnabled = project ? project.capabilities?.usePhases !== false : false;
    const { data: phases = [] } = useQuery({
        queryKey: ['projects', projectId, 'phases'],
        queryFn: () => getProjectPhases(projectId),
        enabled: Boolean(projectId && usePhasesEnabled),
    });

    const useMilestonesEnabled = project ? project.capabilities?.useMilestones !== false : false;
    const isSyntheticPhase = selectedPhaseId === MILESTONE_UNASSIGNED_PHASE_KEY;
    const shouldFetchByPhase = usePhasesEnabled && selectedPhaseId && !isSyntheticPhase;

    const { data: milestonesDetails = [] } = useQuery({
        queryKey: shouldFetchByPhase
            ? ['projects', projectId, 'phases', selectedPhaseId, 'milestones']
            : ['projects', projectId, 'milestones'],
        queryFn: () =>
            shouldFetchByPhase
                ? getProjectMilestonesForPhase(projectId, selectedPhaseId)
                : getProjectMilestones(projectId),
        enabled: Boolean(projectId && useMilestonesEnabled && (selectedPhaseId || !usePhasesEnabled)),
    });

    const milestonesFromProject = useMemo(() => {
        if (milestonesDetails && milestonesDetails.length > 0) {
            return milestonesDetails;
        }
        return [];
    }, [milestonesDetails]);

    const hasUnassignedMilestones = useMemo(
        () => {
            if (usePhasesEnabled) return false;
            return milestonesFromProject.some((m) => m.phaseId == null || m.phaseId === '');
        },
        [usePhasesEnabled, milestonesFromProject],
    );

    const phaseTabs = useMemo(() => {
        const rows = [...phases];
        if (phases.length > 0 && hasUnassignedMilestones) {
            rows.push({
                id: MILESTONE_UNASSIGNED_PHASE_KEY,
                title: 'Unassigned',
                name: 'Unassigned',
                _synthetic: true,
            });
        }
        return rows;
    }, [phases, hasUnassignedMilestones]);

    useEffect(() => {
        if (!phaseTabs.length) {
            setSelectedPhaseId(null);
            return;
        }
        const valid = phaseTabs.some((p) => String(p.id) === String(selectedPhaseId));
        if (!selectedPhaseId || !valid) {
            setSelectedPhaseId(phaseTabs[0].id);
        }
    }, [phaseTabs, selectedPhaseId]);

    const milestonesForPhase = useMemo(() => {
        if (!phases.length) return milestonesDetails;
        if (selectedPhaseId === MILESTONE_UNASSIGNED_PHASE_KEY) {
            return milestonesDetails.filter((m) => m.phaseId == null || m.phaseId === '');
        }
        return milestonesDetails;
    }, [phases.length, selectedPhaseId, milestonesDetails]);

    useEffect(() => {
        if (!milestonesForPhase.length) {
            setSelectedMilestoneId(null);
            return;
        }
        const valid = milestonesForPhase.some((m) => String(m.id) === String(selectedMilestoneId));
        if (!selectedMilestoneId || !valid) {
            setSelectedMilestoneId(milestonesForPhase[0].id);
        }
    }, [milestonesForPhase, selectedMilestoneId]);

    const selectedMilestone = useMemo(
        () => milestonesForPhase.find((m) => String(m.id) === String(selectedMilestoneId)) ?? null,
        [milestonesForPhase, selectedMilestoneId],
    );

    const selectedPhase = useMemo(
        () => (selectedPhaseId != null ? phaseTabs.find((p) => String(p.id) === String(selectedPhaseId)) : null),
        [phaseTabs, selectedPhaseId],
    );

    const milestonePhaseOptions = useMemo(
        () =>
            (phases || []).map((p) => ({
                value: String(p.id),
                label: p.title || p.name || String(p.id),
            })),
        [phases],
    );

    const mentionUsers = useMemo(
        () => (project?.assignedUsers ?? []).slice(0, MENTION_USERS_LIMIT),
        [project?.assignedUsers],
    );
    const mentionSprints = useMemo(() => collectSprintsFromProjectMilestones(project), [project]);
    const mentionMilestones = useMemo(() => milestonesFromProject ?? [], [milestonesFromProject]);

    const [phaseModalMode, setPhaseModalMode] = useState(null);
    const [editingPhaseId, setEditingPhaseId] = useState(null);
    const [phaseForm] = Form.useForm();
    const [phasePendingAttachments, setPhasePendingAttachments] = useState([]);
    const [phasePendingLinkAttachments, setPhasePendingLinkAttachments] = useState([]);
    const [phaseLinkModalOpen, setPhaseLinkModalOpen] = useState(false);
    const [phaseLinkModalForm] = Form.useForm();
    const phaseLinkCallbackRef = useRef(null);

    const [milestoneModalOpen, setMilestoneModalOpen] = useState(false);
    const [milestoneModalMode, setMilestoneModalMode] = useState('create');
    const [editingMilestoneId, setEditingMilestoneId] = useState(null);
    const milestoneModalModeRef = useRef('create');
    const [milestoneForm] = Form.useForm();
    const [editMilestoneForm] = Form.useForm();
    const watchedEditDueDate = Form.useWatch('dueDate', editMilestoneForm);
    const watchedEditPhaseId = Form.useWatch('phaseId', editMilestoneForm);
    const [liveEditDescription, setLiveEditDescription] = useState('');
    const [pendingPostAttachments, setPendingPostAttachments] = useState([]);
    const [pendingLinkAttachments, setPendingLinkAttachments] = useState([]);
    const [editPendingPostAttachments, setEditPendingPostAttachments] = useState([]);
    const [editPendingLinkAttachments, setEditPendingLinkAttachments] = useState([]);
    const [editExistingAttachments, setEditExistingAttachments] = useState([]);
    const [linkModalOpen, setLinkModalOpen] = useState(false);
    const [linkModalForm] = Form.useForm();
    const linkModalCallbackRef = useRef(null);
    const [previewImageUrl, setPreviewImageUrl] = useState(null);

    useEffect(() => {
        milestoneModalModeRef.current = milestoneModalMode;
    }, [milestoneModalMode]);

    /** Latest due in the current phase tab — new milestones must be strictly after this day. */
    const lastMilestoneDueDate = useMemo(
        () => getLastMilestoneDueDate(milestonesForPhase),
        [milestonesForPhase],
    );

    /** Milestones in the phase selected on the edit form (for neighbor due-date bounds). */
    const milestonesForEditDueDate = useMemo(() => {
        if (!milestonesFromProject?.length) return [];
        const pid =
            watchedEditPhaseId !== undefined && watchedEditPhaseId !== '' && watchedEditPhaseId != null
                ? String(watchedEditPhaseId)
                : selectedMilestone?.phaseId != null && selectedMilestone?.phaseId !== ''
                    ? String(selectedMilestone.phaseId)
                    : null;
        if (pid === null || pid === '') {
            return milestonesFromProject.filter((m) => m.phaseId == null || m.phaseId === '');
        }
        return milestonesFromProject.filter((m) => String(m.phaseId) === String(pid));
    }, [milestonesFromProject, watchedEditPhaseId, selectedMilestone]);

    const editDueDateBounds = useMemo(() => {
        if (!editingMilestoneId || !selectedMilestone) return { minExclusive: null, maxExclusive: null };
        const ref =
            watchedEditDueDate != null && dayjs(watchedEditDueDate).isValid()
                ? dayjs(watchedEditDueDate).startOf('day')
                : selectedMilestone.dueDate
                    ? dayjs(selectedMilestone.dueDate).startOf('day')
                    : null;
        if (!ref) return { minExclusive: null, maxExclusive: null };
        return getMilestoneDueDateEditBounds(milestonesForEditDueDate, editingMilestoneId, ref);
    }, [editingMilestoneId, selectedMilestone, milestonesForEditDueDate, watchedEditDueDate]);

    const dueDateRules = useMemo(
        () => [
            { required: true, message: 'Select due date' },
            { validator: createMilestoneDueDateValidator(lastMilestoneDueDate, isRelaxing) },
        ],
        [lastMilestoneDueDate, isRelaxing],
    );

    const disabledDateForPicker = useMemo(() => {
        if (milestoneModalMode === 'edit' && editingMilestoneId) {
            return createMilestoneEditDisabledDate(editDueDateBounds, isRelaxing);
        }
        return createMilestoneDisabledDate(lastMilestoneDueDate, isRelaxing);
    }, [milestoneModalMode, editingMilestoneId, editDueDateBounds, lastMilestoneDueDate, isRelaxing]);

    const editDueDateRules = useMemo(
        () => [{ validator: createMilestoneEditDueDateValidator(editDueDateBounds, isRelaxing) }],
        [editDueDateBounds, isRelaxing],
    );

    const openMilestoneModal = useCallback(() => {
        setMilestoneModalMode('create');
        setEditingMilestoneId(null);
        milestoneForm.resetFields();
        setPendingPostAttachments([]);
        setPendingLinkAttachments([]);
        setMilestoneModalOpen(true);
    }, [milestoneForm]);

    const closeMilestoneModalWithCleanup = useCallback(() => {
        linkModalCallbackRef.current = null;
        setMilestoneModalOpen(false);
        milestoneForm.resetFields();
        editMilestoneForm.resetFields();
        setPendingPostAttachments([]);
        setPendingLinkAttachments([]);
        setEditPendingPostAttachments([]);
        setEditPendingLinkAttachments([]);
        setMilestoneModalMode('create');
        setEditingMilestoneId(null);
        setLiveEditDescription('');
    }, [milestoneForm, editMilestoneForm]);

    const openEditMilestoneModal = useCallback(() => {
        if (!canEditMilestone || !selectedMilestone?.id) return;
        setMilestoneModalMode('edit');
        setEditingMilestoneId(selectedMilestone.id);
        setPendingPostAttachments([]);
        setPendingLinkAttachments([]);
        setEditPendingPostAttachments([]);
        setEditPendingLinkAttachments([]);
        setEditExistingAttachments(selectedMilestone ? getMilestoneAttachmentList(selectedMilestone) : []);
        editMilestoneForm.setFieldsValue({
            name: selectedMilestone.name ?? '',
            description:
                resolveDescriptionMentionsForDisplay(
                    getMilestoneDescriptionText(selectedMilestone),
                    selectedMilestone?.descriptionMentions,
                ) || '',
            dueDate: selectedMilestone.dueDate ? dayjs(selectedMilestone.dueDate) : undefined,
            phaseId:
                selectedMilestone.phaseId != null && selectedMilestone.phaseId !== ''
                    ? String(selectedMilestone.phaseId)
                    : undefined,
            status: normalizeMilestoneStatusForForm(selectedMilestone.status),
            isActive: selectedMilestone.isActive !== false,
        });
        setLiveEditDescription('');
        setMilestoneModalOpen(true);
    }, [canEditMilestone, selectedMilestone, editMilestoneForm]);

    const handleEditMilestoneSubmit = useCallback(() => {
        if (!editingMilestoneId) return;
        editMilestoneForm.validateFields().then((values) => {
            const dueDate = values.dueDate?.format?.('YYYY-MM-DD') ?? values.dueDate;
            const descriptionHtml = contentToApiMentions(values.description ?? '');
            const description = buildMilestoneDescription(descriptionHtml);
            const files = editPendingPostAttachments.map((p) => p.file);
            
            const remainingExisting = editExistingAttachments.map(({ url, title, type }) => ({ url, title, type }));
            const combinedAttachments = [
                ...remainingExisting,
                ...editPendingLinkAttachments.map(({ url, title }) => ({ url, title, type: 'link' })),
            ];

            const phaseId =
                values.phaseId === undefined || values.phaseId === '' || values.phaseId == null
                    ? null
                    : String(values.phaseId);

            const hasAnyAttachments = files.length > 0 || combinedAttachments.length > 0;

            updateMilestone(
                editingMilestoneId,
                {
                    name: (values.name ?? '').trim() || 'Milestone',
                    ...(dueDate ? { dueDate } : {}),
                    ...(description ? { description } : {}),
                    isActive: values.isActive !== false,
                    status: values.status,
                    phaseId,
                    ...(files.length > 0 && { files }),
                    ...(combinedAttachments.length > 0 && { attachments: combinedAttachments }),
                    useMultipart: hasAnyAttachments,
                },
                {
                    onSuccess: () => {
                        message.success('Milestone updated');
                        closeMilestoneModalWithCleanup();
                    },
                    onError: (err) => {
                        message.error(err?.response?.data?.message || err?.message || 'Failed to update milestone');
                    },
                },
            );
        });
    }, [
        editingMilestoneId,
        editMilestoneForm,
        editPendingPostAttachments,
        editPendingLinkAttachments,
        editExistingAttachments,
        updateMilestone,
        closeMilestoneModalWithCleanup,
    ]);

    const handleCreateMilestoneSubmit = useCallback(
        (options = {}) => {
            const { files = [], linkAttachments = [] } = options;
            milestoneForm.validateFields().then((values) => {
                const dueDate = values.dueDate?.format?.('YYYY-MM-DD') ?? values.dueDate;
                const descriptionHtml = contentToApiMentions(values.description ?? '');
                const description = buildMilestoneDescription(descriptionHtml);
                const phaseIdForCreate =
                    phases.length === 0
                        ? undefined
                        : selectedPhaseId === MILESTONE_UNASSIGNED_PHASE_KEY
                            ? null
                            : selectedPhaseId != null
                                ? String(selectedPhaseId)
                                : undefined;
                createMilestone(
                    {
                        name: (values.name ?? '').trim() || 'Milestone',
                        dueDate: dueDate || undefined,
                        ...(phaseIdForCreate !== undefined && { phaseId: phaseIdForCreate }),
                        ...(description && { description }),
                        ...(files?.length > 0 && { files }),
                        ...(linkAttachments?.length > 0 && { attachments: linkAttachments }),
                    },
                    {
                        onSuccess: () => {
                            message.success('Milestone created');
                            closeMilestoneModalWithCleanup();
                        },
                        onError: (err) => {
                            message.error(err?.response?.data?.message || err?.message || 'Failed to create milestone');
                        },
                    },
                );
            });
        },
        [milestoneForm, createMilestone, closeMilestoneModalWithCleanup, phases.length, selectedPhaseId],
    );

    const handleDeleteMilestone = useCallback(() => {
        if (!selectedMilestone?.id) return;
        Modal.confirm({
            title: 'Delete Milestone',
            content: 'Are you sure you want to delete this milestone? This action cannot be undone.',
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: () => {
                deleteMilestone(selectedMilestone.id, {
                    onSuccess: () => {
                        message.success('Milestone deleted');
                        setSelectedMilestoneId(null);
                    },
                    onError: (err) => {
                        message.error(err?.response?.data?.message || 'Failed to delete milestone');
                    },
                });
            },
        });
    }, [selectedMilestone?.id, deleteMilestone, setSelectedMilestoneId]);

    const deletePhaseMutation = useMutation({
        mutationFn: (phaseId) => deleteProjectPhase(projectId, phaseId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'phases'] });
        },
    });

    const handleDeletePhase = useCallback(() => {
        if (!selectedPhase || selectedPhase._synthetic || !selectedPhase.id) return;
        Modal.confirm({
            title: 'Delete Phase',
            content: 'Are you sure you want to delete this phase? This action cannot be undone.',
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: () => {
                deletePhaseMutation.mutate(selectedPhase.id, {
                    onSuccess: () => {
                        message.success('Phase deleted');
                        setSelectedPhaseId(null);
                    },
                    onError: (err) => {
                        message.error(err?.response?.data?.message || 'Failed to delete phase');
                    },
                });
            },
        });
    }, [selectedPhase, deletePhaseMutation, setSelectedPhaseId, projectId, queryClient]);

    useEffect(() => {
        if (milestoneModalOpen) return;
        setPendingPostAttachments((prev) => {
            prev.forEach((p) => {
                if (p.preview) URL.revokeObjectURL(p.preview);
            });
            return [];
        });
        setPendingLinkAttachments([]);
        setEditPendingPostAttachments((prev) => {
            prev.forEach((p) => {
                if (p.preview) URL.revokeObjectURL(p.preview);
            });
            return [];
        });
        setEditPendingLinkAttachments([]);
    }, [milestoneModalOpen]);

    const handlePostFilesSelected = useCallback((files) => {
        if (!files?.length) return;
        const next = files.map((file, i) => {
            const isImg = (file.type || '').startsWith('image/');
            return {
                id: `milestone-${Date.now()}-${i}`,
                file,
                name: file.name,
                type: file.type,
                preview: isImg ? URL.createObjectURL(file) : null,
            };
        });
        setPendingPostAttachments((prev) => [...prev, ...next]);
    }, []);

    const removePendingPostAttachment = useCallback(
        (id) => {
            const index = pendingPostAttachments.findIndex((p) => p.id === id);
            if (index < 0) {
                setPendingPostAttachments((prev) => prev.filter((p) => p.id !== id));
                return;
            }
            const p = pendingPostAttachments[index];
            if (p.preview) URL.revokeObjectURL(p.preview);
            let content = milestoneForm.getFieldValue('description') || '';
            const linkRegex = new RegExp(`<a\\s(?=[^>]*href="#attachment-${index}")[^>]*>[\\s\\S]*?</a>`, 'gi');
            content = content.replace(linkRegex, '');
            const total = pendingPostAttachments.length;
            for (let j = total - 1; j > index; j--) {
                content = content.replace(new RegExp(`#attachment-${j}`, 'g'), `#attachment-${j - 1}`);
            }
            milestoneForm.setFieldValue('description', content);
            setPendingPostAttachments((prev) => prev.filter((item) => item.id !== id));
        },
        [milestoneForm, pendingPostAttachments],
    );

    const handleEditPostFilesSelected = useCallback((files) => {
        if (!files?.length) return;
        const next = files.map((file, i) => {
            const isImg = (file.type || '').startsWith('image/');
            return {
                id: `milestone-edit-${Date.now()}-${i}`,
                file,
                name: file.name,
                type: file.type,
                preview: isImg ? URL.createObjectURL(file) : null,
            };
        });
        setEditPendingPostAttachments((prev) => [...prev, ...next]);
    }, []);

    const removeEditPendingPostAttachment = useCallback(
        (id) => {
            const index = editPendingPostAttachments.findIndex((p) => p.id === id);
            if (index < 0) {
                setEditPendingPostAttachments((prev) => prev.filter((p) => p.id !== id));
                return;
            }
            const p = editPendingPostAttachments[index];
            if (p.preview) URL.revokeObjectURL(p.preview);
            let content = editMilestoneForm.getFieldValue('description') || '';
            const linkRegex = new RegExp(`<a\\s(?=[^>]*href="#attachment-${index}")[^>]*>[\\s\\S]*?</a>`, 'gi');
            content = content.replace(linkRegex, '');
            const total = editPendingPostAttachments.length;
            for (let j = total - 1; j > index; j--) {
                content = content.replace(new RegExp(`#attachment-${j}`, 'g'), `#attachment-${j - 1}`);
            }
            editMilestoneForm.setFieldValue('description', content);
            setEditPendingPostAttachments((prev) => prev.filter((item) => item.id !== id));
        },
        [editMilestoneForm, editPendingPostAttachments],
    );

    const handleRemoveExistingAttachment = useCallback((item) => {
        setEditExistingAttachments((prev) => {
            const itemUrl = item.rawUrl || item.url || item.href;
            const targetResolved = resolveAttachmentUrl(itemUrl);
            return prev.filter((x) => {
                const xUrl = x.url || x.href;
                return resolveAttachmentUrl(xUrl) !== targetResolved;
            });
        });
    }, []);

    const removePendingLinkAttachment = useCallback((id) => {
        setPendingLinkAttachments((prev) => prev.filter((item) => item.id !== id));
    }, []);

    const removeEditPendingLinkAttachment = useCallback((id) => {
        setEditPendingLinkAttachments((prev) => prev.filter((item) => item.id !== id));
    }, []);

    const editMilestoneAttachmentSections = useMemo(() => {
        const links = editExistingAttachments.filter((x) => x?.url && (x.type === 'link' || !isLikelyFileAttachmentUrl(x.url)));
        const files = editExistingAttachments.filter((x) => x?.url && x.type !== 'link' && isLikelyFileAttachmentUrl(x.url));
        return {
            files: files.map((f, idx) => {
                const url = resolveAttachmentUrl(f.url);
                const isImage = isLikelyImageAttachmentUrl(f.url);
                const ext = getAttachmentFileExtension(f.url);
                return {
                    id: f.id ?? `file-${idx}-${f.url}`,
                    url,
                    isImage,
                    ext,
                    title: f.title || 'File',
                    rawUrl: f.url,
                };
            }),
            links: links.map((l, idx) => {
                const href = resolveAttachmentUrl(l.url);
                return {
                    id: l.id ?? `link-${idx}-${l.url}`,
                    href,
                    title: l.title || l.url,
                    rawUrl: l.url,
                };
            }),
        };
    }, [editExistingAttachments]);

    const handleLinkRequest = useCallback(
        (insertLink) => {
            linkModalCallbackRef.current = insertLink;
            linkModalForm.resetFields();
            setLinkModalOpen(true);
        },
        [linkModalForm],
    );

    const closeLinkModal = useCallback(() => {
        setLinkModalOpen(false);
        linkModalCallbackRef.current = null;
    }, []);

    const handleLinkModalSubmit = useCallback(() => {
        linkModalForm.validateFields().then((values) => {
            const url = (values.url || '').trim();
            const title = (values.title || '').trim();
            if (!url) {
                message.warning('Enter URL');
                return;
            }
            const displayTitle = title || url;
            const linkItem = { id: `link-${Date.now()}`, url, title: displayTitle };
            if (milestoneModalModeRef.current === 'edit') {
                setEditPendingLinkAttachments((prev) => [...prev, linkItem]);
            } else {
                setPendingLinkAttachments((prev) => [...prev, linkItem]);
            }
            if (linkModalCallbackRef.current) {
                linkModalCallbackRef.current({ url, title: displayTitle });
                linkModalCallbackRef.current = null;
            }
            setLinkModalOpen(false);
            linkModalForm.resetFields();
        });
    }, [linkModalForm]);

    const handleCreateMilestoneAction = useCallback(() => {
        handleCreateMilestoneSubmit({
            files: pendingPostAttachments.map((p) => p.file),
            linkAttachments: pendingLinkAttachments.map(({ url, title }) => ({ url, title })),
        });
    }, [handleCreateMilestoneSubmit, pendingPostAttachments, pendingLinkAttachments]);

    const closePhaseModal = useCallback(() => {
        setPhaseModalMode(null);
        setEditingPhaseId(null);
        phaseForm.resetFields();
        phaseLinkCallbackRef.current = null;
        setEditExistingAttachments([]);
    }, [phaseForm]);

    const openPhaseModal = useCallback(() => {
        if (!canCreatePhase) return;
        setPhaseModalMode('create');
        setEditingPhaseId(null);
        phaseForm.resetFields();
        setPhasePendingAttachments([]);
        setPhasePendingLinkAttachments([]);
    }, [canCreatePhase, phaseForm]);

    const openEditPhaseModal = useCallback(
        (phase) => {
            if (!canEditPhase || !phase || phase._synthetic) return;
            setPhaseModalMode('edit');
            setEditingPhaseId(phase.id);
            phaseForm.setFieldsValue({
                title: phase.title ?? phase.name ?? '',
                description: resolveDescriptionMentionsForDisplay(
                    getPhaseDescriptionText(phase),
                    phase?.descriptionMentions,
                ),
            });
            setPhasePendingAttachments([]);
            setPhasePendingLinkAttachments([]);
            setEditExistingAttachments(getPhaseAttachmentList(phase));
        },
        [canEditPhase, phaseForm],
    );

    useEffect(() => {
        if (phaseModalMode) return;
        setPhasePendingAttachments((prev) => {
            prev.forEach((p) => {
                if (p.preview) URL.revokeObjectURL(p.preview);
            });
            return [];
        });
        setPhasePendingLinkAttachments([]);
    }, [phaseModalMode]);

    const handlePhasePostFilesSelected = useCallback((files) => {
        if (!files?.length) return;
        const next = files.map((file, i) => {
            const isImg = (file.type || '').startsWith('image/');
            return {
                id: `phase-${Date.now()}-${i}`,
                file,
                name: file.name,
                type: file.type,
                preview: isImg ? URL.createObjectURL(file) : null,
            };
        });
        setPhasePendingAttachments((prev) => [...prev, ...next]);
    }, []);

    const removePhasePendingAttachment = useCallback(
        (id) => {
            const index = phasePendingAttachments.findIndex((p) => p.id === id);
            if (index < 0) {
                setPhasePendingAttachments((prev) => prev.filter((p) => p.id !== id));
                return;
            }
            const p = phasePendingAttachments[index];
            if (p.preview) URL.revokeObjectURL(p.preview);
            let content = phaseForm.getFieldValue('description') || '';
            const linkRegex = new RegExp(`<a\\s(?=[^>]*href="#attachment-${index}")[^>]*>[\\s\\S]*?</a>`, 'gi');
            content = content.replace(linkRegex, '');
            const total = phasePendingAttachments.length;
            for (let j = total - 1; j > index; j--) {
                content = content.replace(new RegExp(`#attachment-${j}`, 'g'), `#attachment-${j - 1}`);
            }
            phaseForm.setFieldValue('description', content);
            setPhasePendingAttachments((prev) => prev.filter((item) => item.id !== id));
        },
        [phaseForm, phasePendingAttachments],
    );

    const removePhasePendingLinkAttachment = useCallback(
        (id) => {
            setPhasePendingLinkAttachments((prev) => prev.filter((item) => item.id !== id));
        },
        [],
    );

    const handlePhaseLinkRequest = useCallback(
        (insertLink) => {
            phaseLinkCallbackRef.current = insertLink;
            phaseLinkModalForm.resetFields();
            setPhaseLinkModalOpen(true);
        },
        [phaseLinkModalForm],
    );

    const closePhaseLinkModal = useCallback(() => {
        setPhaseLinkModalOpen(false);
        phaseLinkCallbackRef.current = null;
    }, []);

    const handlePhaseLinkModalSubmit = useCallback(() => {
        phaseLinkModalForm.validateFields().then((values) => {
            const url = (values.url || '').trim();
            const title = (values.title || '').trim();
            if (!url) {
                message.warning('Enter URL');
                return;
            }
            const displayTitle = title || url;
            setPhasePendingLinkAttachments((prev) => [...prev, { id: `ph-link-${Date.now()}`, url, title: displayTitle }]);
            if (phaseLinkCallbackRef.current) {
                phaseLinkCallbackRef.current({ url, title: displayTitle });
                phaseLinkCallbackRef.current = null;
            }
            setPhaseLinkModalOpen(false);
            phaseLinkModalForm.resetFields();
        });
    }, [phaseLinkModalForm]);

    const handlePhaseModalSubmit = useCallback(() => {
        phaseForm.validateFields().then((values) => {
            const descriptionHtml = contentToApiMentions(values.description ?? '');
            const description = buildPhaseDescription(descriptionHtml);
            const title = (values.title ?? '').trim() || 'Phase';
            const files = phasePendingAttachments.map((p) => p.file);
            const linkAttachments = phasePendingLinkAttachments.map(({ url, title: t }) => ({ url, title: t }));

            if (phaseModalMode === 'create') {
                createPhase(
                    {
                        title,
                        ...(description && { description }),
                        ...(files.length > 0 && { files }),
                        ...(linkAttachments.length > 0 && { attachments: linkAttachments }),
                    },
                    {
                        onSuccess: () => {
                            message.success('Phase created');
                            closePhaseModal();
                        },
                        onError: (err) =>
                            message.error(err?.response?.data?.message || err?.message || 'Failed to create phase'),
                    },
                );
                return;
            }
            if (phaseModalMode === 'edit' && editingPhaseId) {
                const remainingExisting = editExistingAttachments.map(({ url, title, type }) => ({ url, title, type }));
                const combinedAttachments = [
                    ...remainingExisting,
                    ...phasePendingLinkAttachments.map(({ url, title }) => ({ url, title, type: 'link' })),
                ];
                const hasAnyAttachments = files.length > 0 || combinedAttachments.length > 0;
                updatePhase(
                    editingPhaseId,
                    {
                        title,
                        ...(description !== undefined && { description }),
                        ...(files.length > 0 && { files }),
                        ...(combinedAttachments.length > 0 && { attachments: combinedAttachments }),
                        useMultipart: hasAnyAttachments,
                    },
                    {
                        onSuccess: () => {
                            message.success('Phase updated');
                            closePhaseModal();
                        },
                        onError: (err) =>
                            message.error(err?.response?.data?.message || err?.message || 'Failed to update phase'),
                    },
                );
            }
        });
    }, [
        phaseForm,
        phaseModalMode,
        editingPhaseId,
        phasePendingAttachments,
        phasePendingLinkAttachments,
        editExistingAttachments,
        createPhase,
        updatePhase,
        closePhaseModal,
    ]);

    const phaseModalOpen = phaseModalMode != null;

    const handleAddMilestoneDeliverable = useCallback(
        (milestoneId, value) => {
            const v = String(value ?? '').trim();
            if (!v || !milestoneId) {
                message.warning('Enter a deliverable');
                return;
            }
            createMilestoneDeliverable(milestoneId, v, {
                onSuccess: () => message.success('Deliverable added'),
                onError: (err) =>
                    message.error(err?.response?.data?.message || err?.message || 'Failed to add deliverable'),
            });
        },
        [createMilestoneDeliverable],
    );

    const handleToggleMilestoneDeliverable = useCallback(
        (milestoneId, deliverableId, nextMarked) => {
            if (!milestoneId || !deliverableId) return;
            updateMilestoneDeliverableMarked(milestoneId, deliverableId, nextMarked, {
                onError: (err) =>
                    message.error(err?.response?.data?.message || err?.message || 'Failed to update deliverable'),
            });
        },
        [updateMilestoneDeliverableMarked],
    );

    const handleEditMilestoneDeliverable = useCallback(
        (milestoneId, deliverableId, value) => {
            const v = String(value ?? '').trim();
            if (!v || !milestoneId || !deliverableId) {
                message.warning('Enter a valid deliverable text');
                return;
            }
            editMilestoneDeliverable(milestoneId, deliverableId, v, {
                onSuccess: () => message.success('Deliverable updated'),
                onError: (err) =>
                    message.error(err?.response?.data?.message || err?.message || 'Failed to update deliverable'),
            });
        },
        [editMilestoneDeliverable],
    );

    const handleDeleteMilestoneDeliverable = useCallback(
        (milestoneId, deliverableId) => {
            if (!milestoneId || !deliverableId) return;
            deleteMilestoneDeliverable(milestoneId, deliverableId, {
                onSuccess: () => message.success('Deliverable deleted'),
                onError: (err) =>
                    message.error(err?.response?.data?.message || err?.message || 'Failed to delete deliverable'),
            });
        },
        [deleteMilestoneDeliverable],
    );

    const handleRemoveMilestoneAttachment = useCallback(
        (item) => {
            if (!selectedMilestone?.id || !canEditMilestone || !item) return;
            const body = buildRemoveMilestoneAttachmentPatchPayload(selectedMilestone, item);
            if (!body || !Object.keys(body).length) {
                message.warning('Could not remove attachment');
                return;
            }
            updateMilestone(selectedMilestone.id, body, {
                onSuccess: () => message.success('Attachment removed'),
                onError: (err) =>
                    message.error(err?.response?.data?.message || err?.message || 'Failed to remove attachment'),
            });
        },
        [selectedMilestone, canEditMilestone, updateMilestone],
    );

    /** Milestones must be tied to a phase when phases are active; allow create only when the project has at least one phase. */
    const createMilestoneDisabled = useMemo(() => usePhasesEnabled && phases.length === 0, [usePhasesEnabled, phases.length]);
    const createMilestoneDisabledTooltip = useMemo(
        () => (usePhasesEnabled && phases.length === 0 ? 'Add at least one phase before creating a milestone.' : undefined),
        [usePhasesEnabled, phases.length],
    );

    return {
        project,
        phases,
        phaseTabs,
        selectedPhaseId,
        setSelectedPhaseId,
        selectedPhase,
        canViewPayments,
        milestonesFromProject,
        milestonesForPhase,
        selectedMilestoneId,
        setSelectedMilestoneId,
        selectedMilestone,
        canCreateMilestone,
        createMilestoneDisabled,
        createMilestoneDisabledTooltip,
        canEditMilestone,
        canCreatePhase,
        canEditPhase,
        canDeletePhase,
        canDeleteMilestone,
        openPhaseModal,
        openEditPhaseModal,
        phaseModalOpen,
        phaseModalMode,
        closePhaseModal,
        phaseForm,
        handlePhaseModalSubmit,
        handleDeletePhase,
        deletePhasePending: deletePhaseMutation.isPending,
        createPhasePending,
        updatePhasePending,
        phasePendingAttachments,
        phasePendingLinkAttachments,
        handlePhasePostFilesSelected,
        removePhasePendingAttachment,
        handlePhaseLinkRequest,
        phaseLinkModalOpen,
        closePhaseLinkModal,
        phaseLinkModalForm,
        handlePhaseLinkModalSubmit,
        openMilestoneModal,
        openEditMilestoneModal,
        milestoneModalMode,
        milestonePhaseOptions,
        milestoneStatusOptions: MILESTONE_STATUS_OPTIONS,
        editMilestoneForm,
        handleEditMilestoneSubmit,
        updateMilestonePending,
        handleDeleteMilestone,
        deleteMilestonePending,
        handleEditPostFilesSelected,
        removeEditPendingPostAttachment,
        editDueDateRules,
        setLiveEditDescription,
        mentionUsers,
        mentionMilestones,
        mentionSprints,
        milestoneModalOpen,
        closeMilestoneModalWithCleanup,
        milestoneForm,
        dueDateRules,
        disabledDateForPicker,
        createPending,
        handleCreateMilestoneAction,
        pendingPostAttachments,
        pendingLinkAttachments,
        editPendingPostAttachments,
        editPendingLinkAttachments,
        editExistingAttachments,
        editMilestoneAttachmentSections,
        handleRemoveExistingAttachment,
        linkModalOpen,
        closeLinkModal,
        linkModalForm,
        handleLinkRequest,
        handleLinkModalSubmit,
        handlePostFilesSelected,
        removePendingPostAttachment,
        previewImageUrl,
        setPreviewImageUrl,
        handleAddMilestoneDeliverable,
        handleToggleMilestoneDeliverable,
        handleEditMilestoneDeliverable,
        handleDeleteMilestoneDeliverable,
        createDeliverablePending,
        updateDeliverablePending,
        editDeliverablePending,
        deleteDeliverablePending,
        handleRemoveMilestoneAttachment,
        removePendingLinkAttachment,
        removeEditPendingLinkAttachment,
        removePhasePendingLinkAttachment,
    };
}
